import { createStore, type StoreApi } from 'zustand/vanilla'
import { temporal, type TemporalState } from 'zundo'
import {
  applyNodeChanges,
  applyEdgeChanges,
  type Node,
  type Edge,
  type NodeChange,
  type EdgeChange,
} from '@xyflow/react'

export interface DiagramState {
  nodes: Node[]
  edges: Edge[]

  setNodes: (nodes: Node[]) => void
  setEdges: (edges: Edge[]) => void
  addNode: (node: Node) => void
  updateNode: (id: string, patch: Partial<Node>) => void
  removeNode: (id: string) => void
  addEdge: (edge: Edge) => void
  updateEdge: (id: string, patch: Partial<Edge>) => void
  removeEdge: (id: string) => void
  onNodesChange: (changes: NodeChange[]) => void
  onEdgesChange: (changes: EdgeChange[]) => void
  clearHistory: () => void
  setNodesSilent: (nodes: Node[]) => void
  setEdgesSilent: (edges: Edge[]) => void
}

/**
 * The store is augmented by zundo with a `.temporal` substore.
 * Exported as a type alias so consumers can read undo/redo state.
 */
export type DiagramStore = StoreApi<DiagramState> & {
  temporal: StoreApi<TemporalState<Pick<DiagramState, 'nodes' | 'edges'>>>
}

export function createDiagramStore(): DiagramStore {
  const ref: { store: DiagramStore | null } = { store: null }
  // Explicit drag state, decoupled from zundo's internal isTracking flag, so
  // that non-position changes (dimensions/remove/select) arriving mid-drag do
  // not prematurely end coalescing.
  let dragging = false

  // Apply a mutation without it being recorded into zundo history. Used by the
  // explicit silent setters and the select/dimensions "noise" skip.
  const runUntracked = (fn: () => void) => {
    const t = ref.store!.temporal.getState()
    const wasTracking = t.isTracking
    if (wasTracking) t.pause()
    fn()
    if (wasTracking) t.resume()
  }

  const store = createStore<DiagramState>()(
    temporal(
      (set, get) => ({
        nodes: [],
        edges: [],

        setNodes: (nodes) => set({ nodes }),
        setEdges: (edges) => set({ edges }),

        addNode: (node) => set({ nodes: [...get().nodes, node] }),
        updateNode: (id, patch) =>
          set({
            nodes: get().nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)),
          }),
        removeNode: (id) =>
          set({
            nodes: get().nodes.filter((n) => n.id !== id),
            edges: get().edges.filter((e) => e.source !== id && e.target !== id),
          }),

        addEdge: (edge) => set({ edges: [...get().edges, edge] }),
        updateEdge: (id, patch) =>
          set({
            edges: get().edges.map((e) => (e.id === id ? { ...e, ...patch } : e)),
          }),
        removeEdge: (id) => set({ edges: get().edges.filter((e) => e.id !== id) }),

        onNodesChange: (changes) => {
          const temporalApi = ref.store!.temporal.getState()

          // Selection clicks and dimension measurements are noise: apply them
          // untracked so they never pollute undo history. Skip while dragging
          // so interleaved noise is handled by the paused drag path below.
          const noiseOnly =
            changes.length > 0 &&
            changes.every((c) => c.type === 'select' || c.type === 'dimensions')
          if (noiseOnly && !dragging) {
            runUntracked(() => set({ nodes: applyNodeChanges(changes, get().nodes) }))
            return
          }

          const dragStarting = changes.some(
            (c) => c.type === 'position' && c.dragging === true,
          )
          const dragEnded = changes.some(
            (c) => c.type === 'position' && c.dragging === false,
          )

          if (dragStarting && !dragging) {
            // First frame of a drag: apply as a TRACKED set so zundo records
            // the pre-drag state as a single history baseline, then pause so
            // subsequent mid-drag frames don't each become an entry.
            dragging = true
            set({ nodes: applyNodeChanges(changes, get().nodes) })
            if (temporalApi.isTracking) temporalApi.pause()
            return
          }

          if (dragging) {
            // Any change while a drag is active (more drag frames, or an
            // interleaved dimensions/remove change) is applied while paused so
            // it does not create extra history entries. Resume only when the
            // drag actually ends (a position change with dragging:false).
            set({ nodes: applyNodeChanges(changes, get().nodes) })
            if (dragEnded) {
              dragging = false
              if (!temporalApi.isTracking) temporalApi.resume()
            }
            return
          }

          // Not dragging: a normal tracked change.
          set({ nodes: applyNodeChanges(changes, get().nodes) })
        },

        onEdgesChange: (changes) => {
          // EdgeChange has no 'dimensions' variant (edges aren't measured), so
          // 'select' is the only noise type to skip from history.
          const noiseOnly =
            changes.length > 0 && changes.every((c) => c.type === 'select')
          if (noiseOnly) {
            runUntracked(() => set({ edges: applyEdgeChanges(changes, get().edges) }))
            return
          }
          set({ edges: applyEdgeChanges(changes, get().edges) })
        },

        clearHistory: () => ref.store!.temporal.getState().clear(),
        setNodesSilent: (nodes) => runUntracked(() => set({ nodes })),
        setEdgesSilent: (edges) => runUntracked(() => set({ edges })),
      }),
      {
        partialize: (state) => ({ nodes: state.nodes, edges: state.edges }),
        limit: 100,
      },
    ),
  ) as DiagramStore

  ref.store = store
  return store
}
